import { Vec3 } from '@/core/math/Vec3';
import {
  PlayerRecord,
  SAVE_VERSION,
  SaveGame,
  ShipRecord,
} from '@/persistence/WorldRepository';
import { Spacecraft } from '@/spacecraft/Spacecraft';

/**
 * Translation between live simulation objects and the flat records the
 * persistence layer stores.
 *
 * This lives apart from both the game loop and the repository on purpose. The
 * repository must not know what a `Spacecraft` is — swapping IndexedDB for a
 * server implementation should touch no gameplay code — and the game loop must
 * not know what an object store is. The mapping between the two belongs in one
 * file that can be read end to end when the save format changes.
 *
 * Nothing generated is captured here. Terrain, biomes and orbital positions
 * all come back from the seed. What a save contains is the set of facts the
 * seed cannot produce: where the player is, where the vessel is, and what it
 * has burned and broken.
 */

export interface SaveContext {
  seed: number;
  time: number;
  ship: Spacecraft;
  visitedBodies: Set<string>;
  onFoot: boolean;
  characterPositionFixed: Vec3 | null;
  bodyId: string;
}

function shipRecord(ship: Spacecraft, frameBodyId: string): ShipRecord {
  return {
    defId: ship.def.id,
    frameBodyId,
    position: ship.body.position.toArray(),
    velocity: ship.body.velocity.toArray(),
    orientation: [
      ship.body.orientation.x,
      ship.body.orientation.y,
      ship.body.orientation.z,
      ship.body.orientation.w,
    ],
    angularVelocity: ship.body.angularVelocity.toArray(),
    tanks: Array.from(ship.tanks.entries()),
    hullDamage: ship.damage.hull,
    gearDamage: ship.damage.landingGear,
    wingDamage: ship.damage.wings,
    fieldDriveTemperature: ship.fieldDrive?.temperature ?? 0,
    fieldDriveDamage: ship.fieldDrive?.damage ?? 0,
    batteryCharge: ship.power.charge,
    hullTemperature: ship.thermal.temperature,
  };
}

export function buildSave(ctx: SaveContext, id: string, name: string): SaveGame {
  const player: PlayerRecord = {
    location: ctx.onFoot ? ctx.bodyId : 'ship',
    surfacePosition: ctx.characterPositionFixed?.toArray(),
    visitedBodies: Array.from(ctx.visitedBodies),
  };

  return {
    metadata: {
      id,
      name,
      seed: ctx.seed,
      simulationTime: ctx.time,
      savedAt: Date.now(),
      version: SAVE_VERSION,
    },
    player,
    ship: shipRecord(ctx.ship, ctx.bodyId),
    settings: {},
  };
}

export interface RestoreResult {
  ok: boolean;
  reason?: string;
}

/**
 * Applies a save to live systems.
 *
 * Refuses a save from a different seed rather than trying to reconcile it. A
 * save is a set of deltas against a generated world; applied to a *different*
 * world it would put the vessel inside a mountain. Failing loudly is the only
 * honest option.
 */
export function applySave(
  save: SaveGame,
  ctx: SaveContext,
  applyShip: (record: ShipRecord) => boolean,
): RestoreResult {
  if (save.metadata.version !== SAVE_VERSION) {
    return { ok: false, reason: `Save format ${save.metadata.version} is not supported.` };
  }
  if (save.metadata.seed !== ctx.seed) {
    return { ok: false, reason: 'That save belongs to a different universe seed.' };
  }
  if (!applyShip(save.ship)) {
    return { ok: false, reason: `Save refers to an unknown vessel or body.` };
  }

  ctx.visitedBodies.clear();
  for (const id of save.player.visitedBodies) ctx.visitedBodies.add(id);

  return { ok: true };
}
